import {useEffect, useLayoutEffect, useRef} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {hydrateResumableFormDraft} from "@/src/modules/core/lib/form-draft-boot";
import {
  loadActiveResumableFormDraft,
  purgeStaleFormDrafts,
} from "@/src/modules/core/lib/form-draft";
import {consumeFilePickerKillIfAny} from "@/src/modules/core/lib/file-picker-pending";
import {capturePwaKilledDuringFilePickerTelemetry} from "@/src/modules/core/lib/glitchtip/camera-telemetry";
import {resolveFeatureFromWindowLocation} from "@/src/modules/core/lib/glitchtip/resolve-feature";
import {useMeStore} from "@/src/modules/core/store/me";
import {useComplianceBusyStore} from "@/src/modules/core/store/compliance-busy";
import {useFormDraftResumeStore} from "@/src/modules/core/store/form-draft-resume";

let didPurgeStaleFormDrafts = false;

export function FormDraftResume() {
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const mePropertyCount = useMeStore((state) => state.me?.properties?.length ?? 0);
  const pendingDraftKey = useFormDraftResumeStore((state) => state.pending?.draftKey);
  const pendingRoute = useFormDraftResumeStore((state) => state.pending?.route);
  const hydratedDraftKeyRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (didPurgeStaleFormDrafts) {
      return;
    }
    didPurgeStaleFormDrafts = true;
    purgeStaleFormDrafts();
    useComplianceBusyStore.getState().syncFormDraftDeferral();

    const pendingPicker = consumeFilePickerKillIfAny();
    if (pendingPicker) {
      capturePwaKilledDuringFilePickerTelemetry({
        feature: pendingPicker.feature ?? resolveFeatureFromWindowLocation(),
        operation: pendingPicker.operation,
        pickerSource: pendingPicker.source,
      });
    }
  }, []);

  // Fallback hydrate when post-login redirect could not (e.g. /me properties not ready yet).
  useEffect(() => {
    if (mePropertyCount === 0) {
      return;
    }

    const draft = loadActiveResumableFormDraft();
    if (!draft) {
      hydratedDraftKeyRef.current = null;
      return;
    }

    if (pendingDraftKey === draft.draftKey) {
      hydratedDraftKeyRef.current = draft.draftKey;
      return;
    }

    if (hydratedDraftKeyRef.current === draft.draftKey) {
      return;
    }

    const meProperties = useMeStore.getState().me?.properties ?? [];
    hydratedDraftKeyRef.current = draft.draftKey;
    hydrateResumableFormDraft(draft, meProperties);
  }, [mePropertyCount, pendingDraftKey]);

  // Keep the user on the draft route (e.g. after manual navigation to /dars or a typo like /dar).
  useEffect(() => {
    if (!pendingRoute || pathname === pendingRoute) {
      return;
    }
    navigate(pendingRoute, {replace: true});
  }, [navigate, pathname, pendingRoute]);

  return null;
}
